import { useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaGoogleDrive } from 'react-icons/fa';
import { TbBrandOnedrive } from 'react-icons/tb';
import { useGooglePicker } from '@/hooks/useGooglePicker';
import { useOneDrivePicker } from '@/hooks/useOneDrivePicker';
import {
  useGetImportProviderConfigQuery,
  useImportGoogleDriveMutation,
  useImportOneDriveMutation,
} from '@/store/documentsApi';
import {
  CloudImportContainer,
  CloudImportDivider,
  CloudImportButtonGroup,
  CloudImportButton,
} from './CloudImportButtons.styled';

interface CloudImportButtonsProps {
  onImportStarted?: (importJobId: string) => void;
  disabled?: boolean;
}

export default function CloudImportButtons({ onImportStarted, disabled }: CloudImportButtonsProps) {
  const { t } = useTranslation();
  const { openPicker: openGooglePicker, isConfigured: googleConfigured } = useGooglePicker();
  const { openPicker: openOneDrivePicker, isConfigured: oneDriveConfigured } = useOneDrivePicker();
  const [importGoogleDrive] = useImportGoogleDriveMutation();
  const [importOneDrive] = useImportOneDriveMutation();
  const [loading, setLoading] = useState<'google' | 'onedrive' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleDrive = async () => {
    setError(null);
    setLoading('google');
    try {
      const result = await openGooglePicker();
      if (!result || (result.fileIds.length === 0 && result.folderIds.length === 0)) {
        setLoading(null);
        return;
      }
      const job = await importGoogleDrive({
        accessToken: result.accessToken,
        fileIds: result.fileIds,
        folderIds: result.folderIds.length > 0 ? result.folderIds : undefined,
      }).unwrap();
      onImportStarted?.(job.id);
    } catch {
      setError(t('import.pickerError'));
    } finally {
      setLoading(null);
    }
  };

  const handleOneDrive = async () => {
    setError(null);
    setLoading('onedrive');
    try {
      const result = await openOneDrivePicker();
      if (!result || result.items.length === 0) {
        setLoading(null);
        return;
      }
      const job = await importOneDrive({
        accessToken: result.accessToken,
        items: result.items.map((item) => ({
          id: item.id,
          driveId: item.driveId,
          endpoint: item.endpoint,
          name: item.name,
          isFolder: item.isFolder || undefined,
        })),
      }).unwrap();
      onImportStarted?.(job.id);
    } catch {
      setError(t('import.pickerError'));
    } finally {
      setLoading(null);
    }
  };

  const { isLoading: configLoading } = useGetImportProviderConfigQuery();

  if (configLoading) {
    return (
      <CloudImportContainer>
        <CloudImportDivider>{t('import.orImportFrom')}</CloudImportDivider>
        <Spinner animation="border" size="sm" />
      </CloudImportContainer>
    );
  }

  if (!googleConfigured && !oneDriveConfigured) return null;

  return (
    <CloudImportContainer>
      <CloudImportDivider>{t('import.orImportFrom')}</CloudImportDivider>
      <CloudImportButtonGroup>
        {googleConfigured && (
          <CloudImportButton
            onClick={handleGoogleDrive}
            disabled={disabled || loading !== null}
          >
            {loading === 'google' ? <Spinner animation="border" size="sm" /> : <FaGoogleDrive />}
            {t('import.googleDrive')}
          </CloudImportButton>
        )}
        {oneDriveConfigured && (
          <CloudImportButton
            onClick={handleOneDrive}
            disabled={disabled || loading !== null}
          >
            {loading === 'onedrive' ? <Spinner animation="border" size="sm" /> : <TbBrandOnedrive />}
            {t('import.oneDrive')}
          </CloudImportButton>
        )}
      </CloudImportButtonGroup>
      {error && (
        <Alert variant="danger" className="w-100 mt-2" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </CloudImportContainer>
  );
}
